// App-level host for the floating agent chat windows.
//
// The Agents page used to own its chat panels as local state, so leaving the
// page (to Skills, Channels, anywhere) unmounted the panel — and with it the
// WebSocket, cutting off any reply still streaming. The panels now live here,
// above the routed views, and stay open until the user closes them.
//
// Two independent slots, matching what the Agents page offered before: the
// Agent Builder chat and a direct chat with one agent. Both can be open at
// once; opening a slot again with another agent swaps the agent in place.
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import AgentBuilderChat from "../features/agent-chat/AgentBuilderChat";

const AgentChatContext = createContext(null);

export function AgentChatProvider({ children }) {
  // { agent, agents, onClose? } | null
  const [builderChat, setBuilderChat] = useState(null);
  // { agent, agents } | null
  const [directChat, setDirectChat] = useState(null);

  const openBuilderChat = useCallback((agent, { agents, onClose } = {}) => {
    setBuilderChat({ agent, agents, onClose });
  }, []);
  const openDirectChat = useCallback((agent, { agents } = {}) => {
    setDirectChat({ agent, agents });
  }, []);

  const value = useMemo(() => ({ openBuilderChat, openDirectChat }), [openBuilderChat, openDirectChat]);

  return (
    <AgentChatContext.Provider value={value}>
      {children}
      {builderChat && (
        <AgentBuilderChat
          agent={builderChat.agent}
          agents={builderChat.agents}
          onClose={() => {
            const done = builderChat.onClose;
            setBuilderChat(null);
            done?.();
          }}
        />
      )}
      {directChat && (
        <AgentBuilderChat
          agent={directChat.agent}
          agents={directChat.agents}
          onClose={() => setDirectChat(null)}
          directMode
        />
      )}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const ctx = useContext(AgentChatContext);
  if (!ctx) throw new Error("useAgentChat must be inside AgentChatProvider");
  return ctx;
}
